import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// SplitsSection — invoice split UI extracted to its own file so the
// visual-edits babel plugin's MemberExpression analyzer doesn't recurse
// into ManualInvoiceDialog's large render tree alongside it (iter36
// hit a RangeError stack overflow when both lived together).
//
// Props:
//   splits        — current rows
//   onAdd         — add a blank row
//   onUpdate(idx, key, value)
//   onRemove(idx)
//   sumInfo       — { sumLabel, baseLabel, diffLabel, ok }
//
export default function SplitsSection({ splits, onAdd, onUpdate, onRemove, sumInfo }) {
  const rows = splits || [];
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid="manual-splits-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="text-sm font-semibold">Split Among Travellers (per-PAN)</Label>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Each row issues a per-traveller line on the invoice with their own PAN; GST + TCS distributed proportionally.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onAdd}
          data-testid="manual-add-split-btn"
          className="h-8 text-xs"
        >
          <Plus className="h-3 w-3 mr-1" /> Add traveller
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">
          Single-recipient mode (no split). Add a traveller to enable per-PAN split.
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((s, idx) => (
            <SplitRow key={idx} idx={idx} row={s} onUpdate={onUpdate} onRemove={onRemove} />
          ))}
          <div
            data-testid="manual-split-sum-check"
            className={`text-[11px] mt-1 font-mono ${sumInfo.ok ? 'text-emerald-700' : 'text-rose-600'}`}
          >
            Σ Splits = ₹{sumInfo.sumLabel} · Base = ₹{sumInfo.baseLabel}
            {!sumInfo.ok && ` · Δ = ₹${sumInfo.diffLabel}`}
          </div>
        </div>
      )}
    </div>
  );
}

function SplitRow({ idx, row, onUpdate, onRemove }) {
  return (
    <div
      className="grid grid-cols-12 gap-1.5 items-center"
      data-testid={`manual-split-row-${idx}`}
    >
      <Input
        className="col-span-3 h-8 text-xs"
        placeholder="Traveller name"
        value={row.travellerName}
        onChange={(e) => onUpdate(idx, 'travellerName', e.target.value)}
        data-testid={`manual-split-name-${idx}`}
      />
      <Input
        className="col-span-3 h-8 text-xs font-mono uppercase"
        placeholder="PAN (ABCDE1234F)"
        value={row.panCard}
        maxLength={10}
        onChange={(e) => onUpdate(idx, 'panCard', e.target.value.toUpperCase())}
        data-testid={`manual-split-pan-${idx}`}
      />
      <Input
        type="number"
        step="0.01"
        className="col-span-2 h-8 text-xs font-mono"
        placeholder="Amount"
        value={row.amount || ''}
        onChange={(e) => onUpdate(idx, 'amount', Number(e.target.value) || 0)}
        data-testid={`manual-split-amount-${idx}`}
      />
      <Input
        className="col-span-2 h-8 text-xs"
        placeholder="UCC (optional)"
        value={row.ucc}
        onChange={(e) => onUpdate(idx, 'ucc', e.target.value)}
        data-testid={`manual-split-ucc-${idx}`}
      />
      <label
        className="col-span-1 flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer"
        title="Save this PAN to the traveller's profile"
      >
        <input
          type="checkbox"
          className="h-3 w-3"
          checked={!!row.saveToProfile}
          onChange={(e) => onUpdate(idx, 'saveToProfile', e.target.checked)}
          data-testid={`manual-split-save-${idx}`}
        />
        Save
      </label>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={() => onRemove(idx)}
        data-testid={`manual-split-remove-${idx}`}
        className="col-span-1 h-8 w-8 text-rose-400 hover:text-rose-600"
      >
        ×
      </Button>
    </div>
  );
}
